"use client";

import { useGlobalStore } from "@/app/providers/ZustandProvider";
import { Tab, TabItem } from "@/components";
import {
  useCurrentDate,
  useFetchBasket,
  useFetchGachaOffers,
  useFetchHolders,
  useFetchMember,
} from "@/lib/hooks";
import { light_offer_extended } from "@/lib/models/light_offer_extended";
import { filterValidBasketItems } from "@/lib/util";
import {
  FIXED_ACTION_BAR_DRAG_GUARD_ATTR,
  usePreventFixedActionBarDrag,
  usePreventScrollRubberBand,
} from "@/lib/util/usePreventFixedActionBarDrag";
import { ReactNode, useRef } from "react";

interface TabLayoutProps {
  children: ReactNode;
  onTabChange?: (tabId: string) => void;
}

export default function TabLayout({ children, onTabChange }: TabLayoutProps) {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  usePreventFixedActionBarDrag();
  usePreventScrollRubberBand(scrollContainerRef);

  const memberId = useGlobalStore((s) => s.memberId);
  const accessToken = useGlobalStore((s) => s.accessToken);
  const { data: basket = [] } = useFetchBasket(memberId as number);
  const { data: member } = useFetchMember(memberId ?? 0);
  const appId = member?.encrypted_app_id || ""; //
  const { data: holders = [] } = useFetchHolders(Number(memberId));
  const fspNbr = holders[0]?.ref || "";
  const { data: gachaOffers = [] } = useFetchGachaOffers(appId, accessToken, {
    enabled: !!appId && !!accessToken && !!fspNbr,
  });
  const now = useCurrentDate();

  const basketCount = filterValidBasketItems(basket, now).length;
  const gachaClippedCount = gachaOffers.filter(
    (o) => (o as light_offer_extended).keep === 1 && o.used === 0,
  ).length;
  const badgeCount = basketCount + gachaClippedCount;

  const tabs: TabItem[] = [
    {
      id: "offers",
      label: "クーポン",
      href: "/offers",
    },
    {
      id: "basket",
      label: "保存済",
      href: "/basket",
      badgeCount,
    },
  ];

  return (
    <div
      data-testid="tab-layout"
      className="flex flex-col h-screen overflow-hidden"
    >
      <div
        {...{ [FIXED_ACTION_BAR_DRAG_GUARD_ATTR]: "true" }}
        data-testid="tab-layout-header"
        className="fixed top-0 left-0 right-0 z-2000 bg-white"
        style={{ touchAction: "none" }}
      >
        <Tab tabs={tabs} onChange={onTabChange} />
      </div>
      <div
        ref={scrollContainerRef}
        data-testid="tab-layout-content"
        className="flex-1 overflow-y-auto app-scroll pt-16"
      >
        {children}
      </div>
    </div>
  );
}
